import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import * as PATHS from 'js/constants/pathnames';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { TextField } from 'new/elements/TextField';
import { ScreenComponent } from 'new/routes/RouterManager';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { showAlert } from 'new/stores/showAlert/action';
import UserPage from 'new/components/settings/userPage/UserPage';
import { useFetchAdminOrgTiers, useUpdateAdminOrgTier } from 'new/stores/adminOrgs/adminOrgs.api';
import { OrgTier } from 'new/stores/adminOrgs/adminOrgs.types';
import { RoleBanner } from 'new/components/settings/CustomerOrgs/RoleBanner';
import { GuardedControl } from 'new/components/settings/CustomerOrgs/GuardedControl';
import {
  getPlanCategory,
  PLAN_CATEGORY_LABEL,
  sortTiersByCategory,
} from 'new/components/settings/CustomerOrgs/utils';
import styles from './PlanTiers.module.scss';

interface PlanTiersProps {}

interface TierLimits {
  data_source_count_limit: number;
  record_count_limit: number;
  user_members_limit: number;
  trial_period_days: number;
}

function tierLimitsOf(tier: OrgTier): TierLimits {
  return {
    data_source_count_limit: tier.data_source_count_limit ?? 0,
    record_count_limit: tier.record_count_limit ?? 0,
    user_members_limit: tier.user_members_limit ?? 0,
    // -1 (or 0) means "no trial period" — this is the field that determines whether
    // assigning this tier at org creation auto-applies a trial_expires_at.
    trial_period_days: tier.trial_period_days ?? -1,
  };
}

function TierCard({
  tier,
  disabled,
  highlighted,
}: {
  tier: OrgTier;
  disabled: boolean;
  highlighted: boolean;
}) {
  const dispatch = useAppDispatch();
  const [updateTier, { isLoading }] = useUpdateAdminOrgTier();
  const [limits, setLimits] = useState<TierLimits>(tierLimitsOf(tier));

  useEffect(() => {
    setLimits(tierLimitsOf(tier));
  }, [
    tier.id,
    tier.data_source_count_limit,
    tier.record_count_limit,
    tier.user_members_limit,
    tier.trial_period_days,
  ]);

  const onLimitChange =
    (field: keyof TierLimits) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      setLimits((prev) => ({ ...prev, [field]: Number.isNaN(value) ? 0 : value }));
    };

  const onSave = () => {
    updateTier({ id: tier.id, body: limits }).then((result) => {
      if ('error' in result) {
        dispatch(showAlert({ title: `Failed to save ${tier.display_name}`, severity: 'error' }));
        return;
      }
      dispatch(showAlert({ title: `${tier.display_name} updated`, severity: 'success' }));
    });
  };

  return (
    <Card
      id={`tier-${tier.id}`}
      variant="outlined"
      className={highlighted ? styles.tierCardHighlighted : styles.tierCard}
    >
      <CardContent className={styles.cardContent}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
          <div className={styles.tierName}>
            <Typography variant="subtitle2" noWrap>
              {tier.display_name}
            </Typography>
            <Typography variant="body3" color="text.secondary" noWrap display="block">
              {PLAN_CATEGORY_LABEL[getPlanCategory(tier)]} · {tier.name}
            </Typography>
          </div>

          <TextField
            label="User seats"
            type="number"
            size="small"
            className={styles.field}
            disabled={disabled}
            value={limits.user_members_limit}
            onChange={onLimitChange('user_members_limit')}
          />
          <TextField
            label="Data source count"
            type="number"
            size="small"
            className={styles.field}
            disabled={disabled}
            value={limits.data_source_count_limit}
            onChange={onLimitChange('data_source_count_limit')}
          />
          <TextField
            label={`Records (${tier.record_count_limit_time?.toLowerCase()})`}
            type="number"
            size="small"
            className={styles.field}
            disabled={disabled}
            value={limits.record_count_limit}
            onChange={onLimitChange('record_count_limit')}
          />
          <TextField
            label="Trial period (days)"
            helperText="-1 = no trial"
            type="number"
            size="small"
            className={styles.field}
            disabled={disabled}
            value={limits.trial_period_days}
            onChange={onLimitChange('trial_period_days')}
          />

          <GuardedControl disabled={disabled || isLoading}>
            <Button variant="contained" size="small" startIcon={<CheckIcon />} onClick={onSave}>
              Save
            </Button>
          </GuardedControl>
        </Stack>
      </CardContent>
    </Card>
  );
}

export const PlanTiers: ScreenComponent<PlanTiersProps> = () => {
  const pageRef = useRef<HTMLDivElement>();
  const history = useHistory();
  const location = useLocation();
  const user = useCurrentUser();
  const { data: tiers = [] } = useFetchAdminOrgTiers();
  // Always starts locked, even for a super_user, on every mount — same speed-bump pattern
  // as Customer Orgs' RoleBanner: a deliberate "arm before you edit" step, not a security
  // boundary (real authorization is user.super_user, enforced server-side too).
  const [writeArmed, setWriteArmed] = useState(false);
  const canWrite = !!user?.super_user;
  const role = canWrite && writeArmed ? 'full' : 'readonly';

  useEffect(() => {
    if (!user?.super_user || user?.impersonated) {
      history.replace(UserPage.screenConfig.path);
    }
  }, [user?.super_user, user?.impersonated]);

  const highlightedTierId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('tierId');
    return raw ? Number(raw) : null;
  }, [location.search]);

  useEffect(() => {
    if (!highlightedTierId || !tiers.length) return;
    document
      .getElementById(`tier-${highlightedTierId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedTierId, tiers.length]);

  const sortedTiers = useMemo(() => sortTiersByCategory(tiers), [tiers]);

  return (
    <PageLayout
      ref={pageRef}
      title="Plan Tiers"
      className={styles.container}
      description="Manage shared tier definitions. Editing a tier's limits here affects every org currently on that tier, not just one."
    >
      <RoleBanner canWrite={canWrite} writeArmed={writeArmed} onToggleWriteArmed={setWriteArmed} />

      <Stack spacing={1} sx={{ mt: 2 }}>
        {sortedTiers.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            disabled={role !== 'full'}
            highlighted={tier.id === highlightedTierId}
          />
        ))}
      </Stack>
    </PageLayout>
  );
};

PlanTiers.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/plan-tiers`,
  screenName: 'settings.PlanTiers',
  pageTitle: 'Plan Tiers',
  header: {
    title: 'Settings',
    slugTitle: 'Plan Tiers',
  },
  domain: 'settings',
};
